import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Cash code (owner 2026-11-01): before cash is recorded, radaccess sends a code
 * to the login recording it, by SMS, email or both, and that login enters it.
 * POST /access/operator/{ns}/cash-code. A namespace without the code switched
 * on answers 409 cash_code_not_needed, and the sale goes on without one.
 *
 * The code is tied to this login, the customer, the amount and `ref` -- the
 * sale's requestid, or a new subscriber's mobile -- so a retry of the same sale
 * may send it again and no other sale can use it.
 */
export interface CashCodeAsk {
  /** The customer; "" for a new subscriber. */
  account: string;
  amount: number;
  ref: string;
  channels: ("sms" | "email")[];
}

export type CashCodeSent =
  | { needed: false }
  | {
      needed: true;
      requestId: string;
      expiresIn: number;
      resendIn: number;
      sent: string[];
      email?: string;
    };

export async function sendCashCode(
  identity: CallerIdentity,
  ns: string,
  ask: CashCodeAsk,
): Promise<CashCodeSent> {
  try {
    const r = await callGateway<{
      requestId: string;
      expiresIn: number;
      resendIn: number;
      sent: string[];
      email?: string;
    }>(
      identity,
      config().gatewayBase,
      `/access/operator/${encodeURIComponent(ns)}/cash-code`,
      { method: "POST", body: ask },
    );
    return { needed: true, ...r.data };
  } catch (e) {
    if (
      e instanceof ApiError &&
      e.status === 409 &&
      e.message === "cash_code_not_needed"
    )
      return { needed: false };
    throw e;
  }
}

/** What the sale carries: the request id and the 6 digits. */
export function cashCodeToken(requestId: string, code: string): string {
  return `${requestId}.${code.trim()}`;
}

/** The server's refusal codes in words. */
export function cashCodeWords(message: string): string {
  switch (message) {
    case "cash_code_required":
      return "Confirm the cash with the code sent to you.";
    case "cash_code_wrong":
      return "That code is wrong or has expired. Send a new code.";
    case "no_channel_ask_the_owner":
      return "Your login has no verified mobile or email to send the code to. Ask the owner to record this cash.";
    case "code_not_sent_ask_the_owner":
      return "The code could not be sent. Try again, or ask the owner to record this cash.";
    case "user_not_active":
      return "Your login is not active.";
    default:
      return message;
  }
}
